import { AsyncPipe, KeyValuePipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSinglePaneWrapperComponent,
  ElderThemeDirective,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { NumberSequence } from '../number-sequence'
import { VariableFormatDetailComponent } from '../variable-format-detail/variable-format-detail.component'

@Component({
  selector: 'app-number-sequence-detail',
  templateUrl: './number-sequence-detail.component.html',
  styleUrls: ['./number-sequence-detail.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    VariableFormatDetailComponent,
    ElderHeaderComponent,
    AsyncPipe,
    KeyValuePipe,
    ElderTranslatePipe,
  ],
})
export class NumberSequenceDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _sequence$ = new BehaviorSubject<NumberSequence>(null)

  @Input()
  public embedded = false
  @Input()
  public canEditCounter = false
  @Input()
  public canCreate = false
  @Input()
  public canSave = false
  @Input()
  public canDelete = false

  @Output()
  public createRequest = new EventEmitter<NumberSequence>()
  @Output()
  public saveRequest = new EventEmitter<NumberSequence>()
  @Output()
  public deleteRequest = new EventEmitter<NumberSequence>()
  @Output()
  public closeRequest = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get sequence$(): Observable<NumberSequence> {
    return this._sequence$.asObservable()
  }

  public get sequence(): NumberSequence {
    return this._sequence$.getValue()
  }

  @Input()
  public set sequence(sequence: NumberSequence) {
    this._sequence$.next(sequence)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onCreate(event: Event): void {
    if (this.canCreate && !this.sequence) {
      this.createRequest.emit(this.sequence)
    } else {
      this.log.warn('Did not create sequence since creation is disabled!')
    }
  }

  public onSave(event: Event): void {
    if (this.canSave && this.sequence) {
      this.saveRequest.emit(this.sequence)
    } else {
      this.log.warn('Did not save changes since saving is disabled!')
    }
  }

  public onDelete(event: Event): void {
    if (this.canDelete && this.sequence) {
      this.deleteRequest.emit(this.sequence)
    } else {
      this.log.warn('Did not delete sequence since deletion is disabled!')
    }
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private emitClose(event?: Event): void {
    this.closeRequest.emit(event)
  }
}
